<!-- Copyright (c) 2026, NVIDIA CORPORATION.  All rights reserved.

NVIDIA CORPORATION and its licensors retain all intellectual property
and proprietary rights in and to this software, related documentation
and any modifications thereto.  Any use, reproduction, disclosure or
distribution of this software and related documentation without an express
license agreement from NVIDIA CORPORATION is strictly prohibited. -->

<div fxLayout="column" fxFlex class="nv-filter-panel" role="region" [attr.aria-label]="config.title | translate">
    <div fxLayout="column" class="filter-header-section">
        <div fxLayout="row">
            <div class="title" [nvHeadingFocusable]="3">
                {{ config.title | translate }}
            </div>
            @if (config.showCloseButton) {
                <div class="button-close">
                    <button
                        mat-button
                        (click)="onCloseClick()"
                        class="close-button hig-button-icon"
                        [attr.aria-label]="config.closeButtonAriaLabel | translate"
                    >
                        <mat-icon svgIcon="close"></mat-icon>
                    </button>
                </div>
            }
        </div>
        <div class="clear-all-container">
            <button
                #clearAllButton
                [disabled]="clearAllDisabled"
                mat-button
                class="complex-button button-clear-all font-button2"
                (click)="onClearAllClick()"
            >
                {{ config.clearAllText | translate }}
            </button>
        </div>
    </div>

    <mat-accordion multi class="filter-groups-container">
        @for (group of filterGroups; track trackByGroup($index, group); let isFirstGroup = $first) {
            <div class="filter-panel-groups" [class.first-filter-group]="isFirstGroup">
                <!-- Standalone checkboxes (no expansion panel) when group has no label -->
                @if (!group.label) {
                    @for (item of group.items; track trackByItem($index, item)) {
                        <mat-checkbox
                            #firstFilterGroup
                            class="filter-standalone"
                            [tabIndex]="getTabIndex()"
                            [(ngModel)]="item.checked"
                            [disabled]="item.disabled"
                            [aria-label]="item.label | translate"
                            (change)="onFilterItemChange(group, item)"
                        >
                            <span class="font-body1"> {{ item.label | translate }} </span>
                            @if (item.count !== undefined) {
                                <span class="filter-count font-body1"> ({{ item.count }}) </span>
                            }
                        </mat-checkbox>
                    }
                }
                <!-- Expansion panel when group has a label -->
                @if (group.label) {
                    <mat-expansion-panel
                        [class.overlay-screen-mode]="config.overlayMode"
                        [expanded]="group.expanded"
                        (afterExpand)="onPanelOpened(group)"
                        (afterCollapse)="onPanelClosed(group)"
                    >
                        <mat-expansion-panel-header
                            nvMatExpansionPanelHeaderKeyboardFixup
                            cdkMonitorElementFocus
                            #firstFilterGroup
                            class="filter-expansion-panel-header"
                            [class.filter-group-expanded]="group.expanded"
                            [attr.aria-label]="group.label | translate"
                        >
                            <mat-panel-title [attr.aria-hidden]="true">
                                {{ group.label | translate }}
                            </mat-panel-title>
                        </mat-expansion-panel-header>
                        @for (item of group.items; track trackByItem($index, item)) {
                            <mat-checkbox
                                class="filter-checkbox"
                                [tabIndex]="getTabIndex()"
                                [(ngModel)]="item.checked"
                                [disabled]="item.disabled"
                                [aria-label]="item.label | translate"
                                (change)="onFilterItemChange(group, item)"
                            >
                                <div
                                    class="filter-checkbox-label"
                                    [class.disabled-opacity]="item.disabled"
                                    [matTooltip]="item.label | translate"
                                    [matTooltipDisabled]="item.shouldHideTooltip !== false"
                                    matTooltipClass="mat-tooltip-font-body3"
                                    [attr.aria-hidden]="true"
                                >
                                    <span class="font-body1"> {{ item.label | translate }} </span>
                                    @if (item.count !== undefined) {
                                        <span class="filter-count font-body1"> ({{ item.count }}) </span>
                                    }
                                </div>
                            </mat-checkbox>
                        }
                        @if (group.items.length === 0 && group.emptyMessage) {
                            <div class="no-items-message">
                                <span class="font-body2">{{ group.emptyMessage | translate }}</span>
                            </div>
                        }
                    </mat-expansion-panel>
                }
            </div>
        }
    </mat-accordion>
</div>
